import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/common/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { UploadSimple, Trash, Image as ImageIcon } from "@phosphor-icons/react";

export default function SettingsPage() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user?.hospital) {
      const h = user.hospital;
      setForm({
        name: h.name || "",
        address: h.address || "",
        phone: h.phone || "",
        email: h.email || "",
        registration_no: h.registration_no || "",
        nabh_no: h.nabh_no || "",
        mrn_prefix: h.mrn_prefix || "MRN",
        logo_data_url: h.logo_data_url || "",
        header_line1: h.header_line1 || "",
        header_line2: h.header_line2 || "",
        footer_text: h.footer_text || "",
        website: h.website || "",
        reports_enabled_for_users: h.reports_enabled_for_users !== false,
      });
    }
  }, [user]);

  if (!user?.hospital) {
    return (
      <div data-testid="settings-page">
        <PageHeader eyebrow="Settings" title="Hospital settings" />
        <div className="hms-card p-6 text-sm text-[var(--hms-text-2)]">
          You are signed in without a hospital context. Switch to an admin/user account.
        </div>
      </div>
    );
  }
  if (!form) return null;

  const canEdit = user.role === "admin" || user.role === "superuser";

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Logo too large — please use an image under 500 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, logo_data_url: reader.result }));
      toast.success("Logo loaded — click Save to apply");
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    setBusy(true);
    try {
      await api.patch(`/hospitals/${user.hospital_id}`, form);
      toast.success("Hospital details updated — header will appear on every printable PDF");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Settings" title="Hospital settings"
        subtitle="Customize the letterhead that appears on every printable document — admit card, bill receipt, discharge card, and NABH registers." />

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Letterhead preview */}
        <div className="hms-card p-5 lg:col-span-1 space-y-4">
          <div className="hms-section-title">Letterhead preview</div>
          <div className="border border-[var(--hms-border-2)] p-4 bg-white rounded-sm">
            <div className="flex items-start gap-3">
              {form.logo_data_url ? (
                <img src={form.logo_data_url} alt="logo" className="h-14 w-14 object-contain border border-[var(--hms-border)]" />
              ) : (
                <div className="h-14 w-14 grid place-items-center border border-dashed border-[var(--hms-border-2)] text-[var(--hms-muted)]">
                  <ImageIcon size={20} weight="bold" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-heading font-extrabold text-base leading-tight">{form.name || "Hospital name"}</div>
                {form.header_line1 && <div className="text-[11px] text-[var(--hms-text-2)]">{form.header_line1}</div>}
                {form.header_line2 && <div className="text-[10px] text-[var(--hms-muted)]">{form.header_line2}</div>}
              </div>
              <div className="text-right text-[10px] text-[var(--hms-muted)]">
                <div>Reg.: {form.registration_no || "—"}</div>
                <div>NABH: {form.nabh_no || "—"}</div>
              </div>
            </div>
            <div className="border-t border-[var(--hms-ink)] my-2"></div>
            <div className="text-[10px] text-[var(--hms-muted)]">
              {[form.address, form.phone, form.email, form.website].filter(Boolean).join("  |  ") || "—"}
            </div>
            {form.footer_text && (
              <div className="mt-3 pt-2 border-t border-dashed border-[var(--hms-border-2)] text-[9px] text-center text-[var(--hms-muted)]">
                {form.footer_text}
              </div>
            )}
          </div>

          {canEdit && (
            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Logo</Label>
              <input ref={fileRef} type="file" accept="image/*" onChange={onLogoFile} className="hidden" data-testid="logo-input" />
              <div className="flex gap-2">
                <Button type="button" onClick={() => fileRef.current?.click()} variant="outline" className="rounded-sm border-[var(--hms-border-2)] flex-1" data-testid="logo-upload">
                  <UploadSimple size={14} weight="bold" className="mr-1.5" /> Upload logo
                </Button>
                {form.logo_data_url && (
                  <Button type="button" onClick={() => setForm({ ...form, logo_data_url: "" })} variant="outline" className="rounded-sm border-[var(--hms-border-2)] text-[var(--hms-accent)]" data-testid="logo-remove">
                    <Trash size={14} weight="bold" />
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-[var(--hms-muted)]">PNG/JPG/SVG, under 500 KB. Auto-fitted on PDFs.</p>
            </div>
          )}
        </div>

        {/* Form */}
        <div className="hms-card p-5 lg:col-span-2 space-y-3" data-testid="settings-form">
          <div className="hms-section-title">Hospital details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SField label="Hospital name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="set-name" disabled={!canEdit} colSpan={2} />
            <SField label="Header line 1 (tagline / specialization)" value={form.header_line1} onChange={(v) => setForm({ ...form, header_line1: v })} testid="set-h1" disabled={!canEdit} colSpan={2} />
            <SField label="Header line 2 (accreditation, est. year, etc.)" value={form.header_line2} onChange={(v) => setForm({ ...form, header_line2: v })} testid="set-h2" disabled={!canEdit} colSpan={2} />
            <SField label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="set-phone" disabled={!canEdit} />
            <SField label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="set-email" disabled={!canEdit} />
            <SField label="Website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} testid="set-web" disabled={!canEdit} />
            <SField label="Registration No." value={form.registration_no} onChange={(v) => setForm({ ...form, registration_no: v })} testid="set-reg" disabled={!canEdit} />
            <SField label="NABH No." value={form.nabh_no} onChange={(v) => setForm({ ...form, nabh_no: v })} testid="set-nabh" disabled={!canEdit} />
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">MRN Prefix</Label>
              <Input
                value={form.mrn_prefix}
                onChange={(e) => setForm({ ...form, mrn_prefix: e.target.value.toUpperCase() })}
                disabled={!canEdit}
                pattern="^[A-Z0-9_\-]{1,16}$"
                maxLength={16}
                title="1-16 chars: A-Z, 0-9, dash or underscore. No slashes or spaces."
                placeholder="e.g. MRN, CITY-1, WARD_A"
                data-testid="set-prefix"
                className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white font-mono"
              />
              <p className="text-[10px] text-[var(--hms-muted)] mt-1">URL-safe only: A–Z, 0–9, dash, underscore (≤16 chars).</p>
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Address</Label>
              <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} disabled={!canEdit}
                className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" data-testid="set-addr" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">Footer text (printed at bottom of every PDF)</Label>
              <Textarea rows={2} value={form.footer_text} onChange={(e) => setForm({ ...form, footer_text: e.target.value })} disabled={!canEdit}
                placeholder="e.g. Patient feedback: care@hospital.com  |  24x7 emergency: 1800-XXX-XXX  |  This bill is system-generated"
                className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" data-testid="set-footer" />
            </div>
            <div className="md:col-span-2 flex items-center gap-3 pt-3 border-t border-dashed border-[var(--hms-border)]">
              <input
                id="reports-toggle"
                type="checkbox"
                checked={!!form.reports_enabled_for_users}
                onChange={(e) => setForm({ ...form, reports_enabled_for_users: e.target.checked })}
                disabled={!canEdit}
                data-testid="set-reports-enabled"
                className="h-4 w-4 rounded-sm border-[var(--hms-border-2)] accent-[var(--hms-accent)] cursor-pointer disabled:cursor-not-allowed"
              />
              <Label htmlFor="reports-toggle" className="text-sm font-medium cursor-pointer">
                Show <span className="font-semibold">Reports</span> tab to staff (user role)
              </Label>
              <span className="text-[11px] text-[var(--hms-muted)] ml-auto">
                Admins/superusers always see Reports.
              </span>
            </div>
          </div>
          {canEdit && (
            <div className="pt-2">
              <Button type="submit" disabled={busy} data-testid="set-submit" className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
                {busy ? "Saving…" : "Save header"}
              </Button>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

function SField({ label, value, onChange, testid, disabled, colSpan }) {
  return (
    <div className={colSpan === 2 ? "md:col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        data-testid={testid} className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white" />
    </div>
  );
}
